import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { groupsAPI, goalsAPI } from '../services/api';
import HotseatScheduler from '../components/HotseatScheduler';

const GroupDetail = () => {
  const { id } = useParams();
  const [group, setGroup] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchGroupData();
  }, [id]);

  useEffect(() => {
    if (group) {
      fetchGoals();
    }
  }, [group, selectedDate]);

  const fetchGroupData = async () => {
    try {
      const response = await groupsAPI.getById(id);
      setGroup(response.data);
    } catch (error) {
      console.error('Error fetching group:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await goalsAPI.getByGroup(id);
      const filteredGoals = response.data.filter(goal => 
        goal.meeting_date === selectedDate
      );
      setGoals(filteredGoals);
    } catch (error) {
      console.error('Error fetching goals:', error);
      setGoals([]);
    }
  };

  const generateWhatsAppMessage = (member) => {
    const memberGoals = goals.filter(goal => goal.member_id === member.id);
    
    let message = `📋 *${group.name} - ${member.name}*\n\n`;
    
    if (memberGoals.length > 0) {
      message += `Goals for ${selectedDate}:\n`;
      memberGoals.forEach(goal => {
        message += `• ${goal.goal_text}\n`;
      });
    } else {
      message += `No goals set for ${selectedDate}\n`;
    }
    
    return encodeURIComponent(message);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!group) {
    return <div className="text-center text-red-600">Group not found</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{group.name}</h1>
        <div className="text-gray-600">
          <p><span className="font-medium">Schedule:</span> {group.schedule}</p>
          <p><span className="font-medium">Time:</span> {group.time}</p>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meeting Month
        </label>
        <input
          type="month"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Hotseat Scheduler */}
      <div className="mb-8">
        <HotseatScheduler 
          group={group} 
          onUpdate={() => {
            // Refresh data when hotseats are updated
            fetchGroupData();
          }}
        />
      </div>

      {/* Member Goals */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Member Goals for {selectedDate}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {group.members && group.members.filter(m => m).map(member => {
            const memberGoals = goals.filter(goal => goal.member_id === member.id);
            
            return (
              <div key={member.id} className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{member.name}</h3>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">
                    Goals for {selectedDate}:
                  </h4>
                  {memberGoals.length > 0 ? (
                    <ul className="space-y-2">
                      {memberGoals.map((goal, index) => (
                        <li key={index} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          {goal.goal_text}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No goals set for this month</p>
                  )}
                </div>

                <a
                  href={`https://wa.me/?text=${generateWhatsAppMessage(member)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  📱 WhatsApp Message
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GroupDetail;